import { Config } from 'config';

import { forEach, filter, includes, now, assign } from 'lodash';

import { setLineDash } from 'gear';

import { Point, Frame, updateArray$ } from 'cdl';

import { Tool } from '../view';

import { OptimizedFiguresHandles } from './select-transform';

import { DragSelectorHelper } from './drag-selector';

export const SelectBaseTool = Tool.extend({

  type: 'Select',

  toolbarName: 'selectTool',
  allowKeyShortcuts: true,
  isCommitable: false,

  Properties: {
    selectAndDragEnabled: { type: 'boolean', def: true },
  },

  resume() {
    this.callParent();
    this.handles.update();
    this.setIddleToolhelp();
  },

  initTool( view, config ) {
    assign(this, config || {});

    this.handles = new OptimizedFiguresHandles({ view, tool: this,

      editingItems( view ) {
        return view.selectedFigures;
      },

      onItemsDrag( event, figures ) {
        view.fireEvent('figuresdrag',event,figures);
      },
      onItemsDrop({ view, event }, figures ) {
        const onDrop = { cancelMove: false };
        if( event ) {
          view.fireEvent('figuresdrop',event,figures,onDrop); // [TODO] FiguresDropEvent
        }

        const cancelMove = onDrop.cancelMove;
        if( cancelMove ) {
          this.absoluteTranslate( new Point(0,0) );
        }

        const notRemovedFigures = [];
        const { doc } = view;
        forEach( figures, figure => {
          if( figure.document === doc ) {
            notRemovedFigures.push( figure );
          }
        });
        return notRemovedFigures.length > 0 && ! cancelMove;
      },

      setIddleToolhelp: () => this.setIddleToolhelp()
    });

    this._transparentMode = false;
  },

  setIddleToolhelp() {
    const th = ['Click or draw rectangle to select','Ctrl and drag Canvas to pan'];
    if( this.view.selectedFigures.length > 0 ) {
      th.push('Shift to aggregate to selection');
      th.push('Alt to make selected figures transparent');
      th.push('Alt-Click to select figure below selection');
    }
    this.toolhelp(th);
  },

  link( view ) {
    view.on({
      'rendercomplete': this.on_rendercomplete//,
      //'change' : this.on_change,
      //'resize' : this.on_change
    }, this );

    this.handles.create();
  },

  unlink( view ) {
    view.un(this);

    this.handles.dispose();
  },

  on_dblclick({ view, docPoint }) {

    const selectedFigures = view.selectedFigures;

    if( selectedFigures.length === 1 ) {
      const figure = selectedFigures[0];
      if( view.canEditContent(figure) ) { // [TODO] && view._dblclickContentEditionEnabled(figure) ) {
        view.editContent(figure,docPoint);
      }
    }
    else if( view.editingContent() ) {
      view.commitContent$();
    }
  },

  onSelectionChanged(/* view */) {
    /*
    const tm = this._transparentMode;
    if( tm ) {
      forEach( this._transparentFigureViews, fv => { fv.hideMask(); });
      this._transparentMode = false;
      this.on_keydown_alt(view);
    }
    */
    this.setIddleToolhelp();
    this.redraw();
  },

  draw( config ) {

    // Draw selected texture island, we should make this a plugin
    // [TODO] Hacking in missing features from v5, later clean up
    const { view, context, transformModel } = config;
    const docIslands = filter( view.selectedFigures, figure => figure.metadata.get('textureIslandName') );
    if( view.is3D && view.model && docIslands.length > 0 ) {
      forEach( docIslands, docIsland => {
        const modelIsland = view.model.modelIslandFor(docIsland);
        context.strokeStyle = '#f2f2f2';
        context.lineWidth = 25;
        modelIsland.draw(context,transformModel,{ customDraw: c => c.stroke() });
        context.strokeStyle = '#388638';
        context.lineWidth = 20;
        setLineDash(context,[6,4]);
        modelIsland.draw(context,transformModel,{ customDraw: c => c.stroke() });
      });
    }
    else {

      this.handles.draw(config);
    }
  },

  useAggregateMode({ shiftKey, cmdKey }) {
    return shiftKey || ( Config.ctrlAggregation && cmdKey );
  },
  useContentEditMode({ cmdKey }) {
    return ! Config.ctrlAggregation && cmdKey;
  },

  try_pointerdown({ view, event }) {

    const { handles } = this;

    if( handles.isHandle(event) ) {

      if( handles.isHandleEnabledAt(event) ) {
        handles.down(event);
        event.stopPropagation();
      }

      // Clicking selection
      // If there is no mouse movement for a few milliseconds,
      // we should select this figure in the next mouse up.
      this._possibleClicking = { event, time: now() };

      return true;
    }

    return false;
  },

  on_pointerdown({ view, event, which, cmdKey, docPoint, pointers }) {

    const { handles } = this;

    if( pointers.length === 2 ) {

      if( handles._editionMode ) {
        event.stopPropagation();
        return;
      }

      this._possibleDrag = null;
      this.helper = null;
      return;
    }

    if( handles._editionMode ) {
      this._endMode(event);
      return;
    }

    const leftClick = which === 1;
    if( ! leftClick ) {
      // right click on empty design area?
      if( ! event.figure ) { // [EVENTS] some( view.selectedFigures, figure => figure.canBeSelected() && figure.frame_().contains(docPoint) )) {
        // [TODO] stop event?
        view.clearSelection();
        view.render$();
      }
      return ;
    }

    if( ! this.try_pointerdown(event) ) {

      const { figureHit } = event; // get top most figure at point

      const { handles } = this;

      const possibleDrag = () => {
        if( ! cmdKey ) {
          if( ! this.useAggregateMode(event) ) {
            view.clearSelection();
          }
          view.render$();
          this._possibleDrag = { event };
        }
      };

      if( figureHit ) { // click directly on figure (not handle)?

        const { figure, tight } = figureHit;
        if( tight && this.selectAndDragEnabled && ! ( view.is3D && ! view.selectAndDragEnabled ) ) { // [TODO] Review...

          this._figureClicked( figure, event );

          // [TODO] Review, hacking my way to production now, allow to select and drag the 3D model
          // in case the figure is not movable

          const movable = view.is(figure,'movable');
          if( movable ) {
            event.stopPropagation();
            handles.update(); // Needed for select and drag
            handles.down(event);
          }

          view.render$();
        }
        else {
          this._possibleClicking = { event, time: now() };
          possibleDrag();
        }

        return;
      }
      else { // click on empty design area?
        possibleDrag();
      }
    }
  },

  on_mousemove( event ) {
    this.handles.mousemove(event);
  },

  on_pointermove({ view, event }) {
    const { _possibleDrag } = this;
    if( _possibleDrag ) {
      this.helper = new DragSelectorHelper(_possibleDrag.event,this._dragEnd.bind(this));
      this._possibleDrag = null;
    }
    this.handles.move(event);
  },

  on_pointerup( event ) {
    this._endMode(event);
  },

  _endMode({ view, event, viewPoint }) {

    this.handles.up(event);

    this._possibleDrag = null;

    const pc = this._possibleClicking;
    if( pc ) {
      const t = now();
      if( t - pc.time < 500 ) {
        const pc_event = pc.event;
        const pcViewPoint = pc_event.viewPoint;

        // Allow small shifts, accepted as clicks
        if( Math.abs(pcViewPoint.x-viewPoint.x) <= 1 &&
            Math.abs(pcViewPoint.y-viewPoint.y) <= 1 ) {

          const { figure } = pc.event;

          if( figure ) {
            // Add the selected figure
            this._figureClicked( figure, pc_event );
            view.render$();
          }
          else if( this.useContentEditMode(pc_event) && view.editingContent() ) {
            view.commitContent$();
          }
        }
      }
      this._possibleClicking = null;
    }
  },

  on_rendercomplete() {
    this.handles.update();
  },

  _dragEnd({ view, event }, bounds ) {

    if( bounds.width > 0 && bounds.height > 0 ) {

      const { selectedFigures } = view;
      const figures = view.activeSelectableFigures(); // [TODO]

      updateArray$(figures) .then( () => {

      const selection = filter( figures, figure => {

        // Is Inside Bounds
        const fb = figure.bounds_();
        return fb.lx > bounds.lx &&
               fb.ly > bounds.ly &&
               fb.hx < bounds.hx &&
               fb.hy < bounds.hy ;
      });

      if( selection.length > 0 ) {
        // Force selection of upper layer figures first
        selection.reverse();

        selectedFigures.add(selection);
      }

      view.render$();
      });
    }
  },

  _figureClicked( figure, event ) {
    const view = this.view;
    const selectedFigures = view.selectedFigures;

    view.activeDocument = figure.document;

    if( this.useContentEditMode(event) &&
        view.canEditContent(figure) ) { // [TODO] && view._dblclickContentEditionEnabled(figure) ) {
      return view.editContent(figure,view.docPoint(event));
    }

    if( this.useAggregateMode(event) ) {

      if( includes(selectedFigures,figure) ) {
        selectedFigures.remove(figure);
      }
      else {
        selectedFigures.add(figure);
      }
    }
    // Avoid selectionChanged event in case it is already selected
    else if( ! ( selectedFigures.length === 1 && selectedFigures[0] === figure ) ) {

      selectedFigures.set( [ figure ] );
    }

  },

  somethingToDraw() {
    return this.handles.visible;
  },

  needsToDrawFor( docIsland ) {
    const { frame } = this.handles;
    return !!frame && Frame.areOverlapping( docIsland.frame_(), this.handles.frame );
  }
});

export const SelectTool = SelectBaseTool.extend({ typeName: 'SelectTool',

  canUndo() {
    return this.view.doc.canUndo();
  },
  undo() {
    return this.view.doc.undo();
  },

  canRedo() {
    return this.view.doc.canRedo();
  },
  redo() {
    return this.view.doc.redo();
  },

  supportsClipboard() {
    return true;
  }
});
Tool.Select = SelectTool;



/** WEBPACK FOOTER **
 ** ./src/ui/tool/select.js
 **/